/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * uploadAdapter — routes between Stage 1 (FastAPI chunked relay) and
 * Stage 2 (direct-to-R2 multipart) based on a layered policy.
 *
 * Layered decision (highest precedence first):
 *
 *   1. **Hard escape hatch** — `localStorage.lr_force_stage1 === "true"`
 *      forces Stage 1 regardless of every other signal. Lets a user
 *      who hits an R2/CORS/network edge case opt themselves back to
 *      the proven path without a redeploy.
 *
 *   2. **Hard force-on** — `localStorage.lr_use_cloud_upload === "true"`
 *      forces Stage 2. Used for QA + per-device opt-in testing.
 *
 *   3. **Auto mode** — when `REACT_APP_USE_CLOUD_UPLOAD === "auto"`,
 *      the adapter routes based on file size + role:
 *        • file size ≥ 500 MB AND
 *        • role is instructor-side (adi / pdi / instructor / admin)
 *          (pupil uploads stay on Stage 1 during phase-1 rollout)
 *      → Stage 2. Otherwise Stage 1.
 *
 *   4. **Hard env-on** — `REACT_APP_USE_CLOUD_UPLOAD === "true"`
 *      forces Stage 2 for every upload. Stays as the "ship it" flip
 *      once the auto-mode rollout proves stable.
 *
 *   5. **Default** — Stage 1.
 *
 * `adaptiveUpload` no longer falls back silently. P0 May 24, 2026 —
 * operator doctrine — when Stage 2 is selected and `/api/uploads/cloud/init`
 * fails (R2 not configured, CORS misconfigured, R2 outage), the adapter
 * now raises the error to the caller terminally. Silent fallback to
 * Stage 1 was relocating bytes onto /app and is forbidden.
 */
import { chunkedUpload } from "./chunkedUpload";
import { cloudUpload } from "./cloudUpload";
export {
  AuthExpiredError,
  UploadPausedError,
  formatBytes,
} from "./chunkedUpload";

// 500 MB — the size class that consistently breaks Stage 1 under
// Cloudflare's 100 s proxy timeout during /chunked-complete assembly.
// Files below this threshold finalise comfortably on Stage 1.
export const CLOUD_UPLOAD_AUTO_THRESHOLD_BYTES = 500 * 1024 * 1024;

// Roles allowed to take the Stage 2 path during phase-1 rollout.
// Pupils stay on Stage 1 until R2 is proven stable across the larger
// instructor cohort.
const STAGE2_ROLLOUT_ROLES = new Set(["adi", "pdi", "instructor", "admin"]);

function _ls(key) {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage?.getItem(key) || null;
  } catch { return null; }
}


/**
 * Decide which upload pipeline to use for the given context.
 *
 * @param {{file?: File, fileSize?: number, role?: string}} ctx
 * @returns {{useCloud: boolean, reason: string}}
 *   `reason` is a stable string suitable for logging — values:
 *   `escape_hatch_force_stage1`, `force_on_localstorage`,
 *   `auto_size_role_match`, `auto_under_threshold`,
 *   `auto_role_excluded`, `env_force_on`, `env_disabled`.
 */
export function decideCloudUpload(ctx = {}) {
  const force1 = _ls("lr_force_stage1");
  if (force1 === "true") {
    return { useCloud: false, reason: "escape_hatch_force_stage1" };
  }
  const force2 = _ls("lr_use_cloud_upload");
  if (force2 === "true") {
    return { useCloud: true, reason: "force_on_localstorage" };
  }
  const env = (process.env.REACT_APP_USE_CLOUD_UPLOAD || "").toLowerCase();
  if (env === "true") {
    return { useCloud: true, reason: "env_force_on" };
  }
  if (env === "auto") {
    const size = typeof ctx.fileSize === "number"
      ? ctx.fileSize
      : (ctx.file?.size || 0);
    const role = (ctx.role || "").toLowerCase();
    if (!STAGE2_ROLLOUT_ROLES.has(role)) {
      return { useCloud: false, reason: "auto_role_excluded" };
    }
    if (size >= CLOUD_UPLOAD_AUTO_THRESHOLD_BYTES) {
      return { useCloud: true, reason: "auto_size_role_match" };
    }
    return { useCloud: false, reason: "auto_under_threshold" };
  }
  return { useCloud: false, reason: "env_disabled" };
}


/**
 * Back-compat wrapper used by older call-sites (e.g. LessonReview's
 * `upload.path_selected` log). Returns a plain bool by calling
 * `decideCloudUpload({})` with no context — matches pre-rollout
 * behaviour where the flag was a coarse on/off.
 */
export function isCloudUploadEnabled(ctx) {
  return decideCloudUpload(ctx || {}).useCloud;
}


function _logRouting(decision, ctx) {
  if (typeof console === "undefined" || !console.info) return;
  try {
    const safe = {
      stage: decision.useCloud ? "stage_2_cloud_r2" : "stage_1_chunked_relay",
      reason: decision.reason,
      file_size_mb: ctx?.file?.size
        ? Math.round(ctx.file.size / (1024 * 1024))
        : (typeof ctx?.fileSize === "number"
          ? Math.round(ctx.fileSize / (1024 * 1024))
          : null),
      role: ctx?.role || null,
    };
    console.info("[lr-upload] upload.routing_decision", safe);
  } catch { /* logging must never throw */ }
}


export async function adaptiveUpload(opts) {
  const decision = decideCloudUpload({
    file: opts?.file,
    role: opts?.userRole,
  });
  _logRouting(decision, { file: opts?.file, role: opts?.userRole });

  if (!decision.useCloud) {
    return chunkedUpload(opts);
  }

  // Stage 2 path.
  //
  // P0 May 24, 2026 (operator doctrine) — the previous silent fallback
  // from cloud-init failure to chunkedUpload was REMOVED. Reason: a
  // 30-second R2 hiccup used to push N parallel lesson uploads back
  // onto /app local disk, which is exactly the disk-pressure attack
  // vector the platform pivoted away from. R2 failure must now fail
  // loud and terminal so the UI can surface a clear error and the
  // operator can choose to retry on the cloud path rather than have
  // bytes silently relocate to the host filesystem.
  //
  // The cloudUpload layer raises a stable error contract that the
  // caller can match (cloud_init_failed, R2 multipart create failed,
  // Cloud uploads not configured, Init failed). Callers wanting
  // explicit fallback behaviour must opt in by catching at their own
  // layer — there is no longer an automatic chunked fallback here.
  return await cloudUpload(opts);
}
